/**
 * Detecting a relay lane that goes quiet while the client is waiting on the host.
 *
 * The host's `closed` notice is a best-effort publish, and a lane can stop
 * delivering without any socket event. copilot-host's connection lifecycle says
 * to treat a lane that stays silent while traffic is expected, such as during a
 * turn or while a dispatch awaits its echo, as ended.
 *
 * The watchdog only runs while something is expected, so it never probes an
 * idle session; the runtime's keep-alive pings idle lanes separately. A `ping`
 * answered by a later host connection proves nothing on its own; the transport
 * ends the connection when that happens, so the probe only has to report
 * whether this connection answered.
 */

/** Inbound silence, while traffic is expected, after which the lane is probed. */
export const EXPECTED_TRAFFIC_SILENCE_MS = 60 * 1000

/**
 * Answered probes in a row, with no host notification between them, after
 * which the watchdog stops probing on its own. A turn can run a silent tool for
 * a long time, and this bounds what a turn the client wrongly believes is still
 * running can cost. Host traffic restarts the count, and so does a new dispatch
 * reported through {@link RelayTrafficWatchdog.expectReply}.
 */
export const MAX_SILENT_PROBES = 10

/**
 * - `answered`: this host connection replied, so its lane still delivers.
 * - `unanswered`: nothing replied in time, so the connection should end.
 * - `superseded`: the connection changed while probing, so the result is moot.
 */
export type LaneProbeResult = 'answered' | 'unanswered' | 'superseded'

export interface RelayTrafficWatchdogOptions {
  /** Whether the client is currently waiting on host traffic. */
  expectsTraffic(): boolean
  /** Round-trips a `ping` on the current connection. */
  probe(): Promise<LaneProbeResult>
  /** Ends the connection whose lane stayed silent. */
  onSilent(): void
  /** Runs after an answered probe with the time the probe was sent. */
  onAnswered?(probeSentAtMs: number): void
  silenceMs?: number
}

export class RelayTrafficWatchdog {
  readonly #options: RelayTrafficWatchdogOptions
  readonly #silenceMs: number
  #epoch = 0
  #running = false
  #quietSinceMs = 0
  #timer: ReturnType<typeof setTimeout> | undefined
  #probing = false
  #silentProbes = 0

  constructor(options: RelayTrafficWatchdogOptions) {
    this.#options = options
    this.#silenceMs = options.silenceMs ?? EXPECTED_TRAFFIC_SILENCE_MS
  }

  /** Starts watching a newly established connection. */
  start(): void {
    this.stop()
    this.#running = true
    this.#silentProbes = 0
    this.expect()
  }

  /** Stops watching, and discards the result of any probe still in flight. */
  stop(): void {
    this.#running = false
    this.#epoch++
    this.#probing = false
    if (this.#timer !== undefined) clearTimeout(this.#timer)
    this.#timer = undefined
  }

  /**
   * Records an inbound frame. Any frame shows the lane delivers, but only a
   * host notification or request, not a reply to this client's own request,
   * is the kind of traffic a turn produces.
   */
  noteInbound(fromHost: boolean): void {
    this.#quietSinceMs = Date.now()
    if (!fromHost || this.#silentProbes === 0) return
    const exhausted = this.#silentProbes >= MAX_SILENT_PROBES
    this.#silentProbes = 0
    if (exhausted) this.expect()
  }

  /**
   * Arms the watchdog when the client may have started waiting on the host,
   * unless answered probes have used up its budget.
   */
  expect(): void {
    if (!this.#running || this.#timer !== undefined || this.#probing) return
    if (this.#silentProbes >= MAX_SILENT_PROBES || !this.#options.expectsTraffic()) return
    // Waiting starts now, so an idle period before it is not counted as silence.
    this.#quietSinceMs = Date.now()
    this.#arm(this.#silenceMs)
  }

  /**
   * Arms the watchdog for a new dispatch the host owes a reply to. The client is
   * newly waiting on the host, so probes that a quiet stretch before it used up
   * do not count against it.
   */
  expectReply(): void {
    this.#silentProbes = 0
    this.expect()
  }

  #arm(delayMs: number): void {
    const epoch = this.#epoch
    this.#timer = setTimeout(() => void this.#check(epoch), delayMs)
  }

  async #check(epoch: number): Promise<void> {
    this.#timer = undefined
    if (epoch !== this.#epoch || !this.#running || !this.#options.expectsTraffic()) return
    const silentForMs = Date.now() - this.#quietSinceMs
    if (silentForMs < this.#silenceMs) {
      this.#arm(this.#silenceMs - silentForMs)
      return
    }
    this.#probing = true
    const sentAtMs = Date.now()
    const result = await this.#options.probe()
    if (epoch !== this.#epoch) return
    this.#probing = false
    if (result === 'unanswered') {
      this.stop()
      this.#options.onSilent()
      return
    }
    if (result === 'superseded') return
    this.#silentProbes++
    this.#options.onAnswered?.(sentAtMs)
    this.expect()
  }
}
